import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

export const FormCardSkeleton = ({ className = "" }) => (
    <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col relative overflow-hidden group ${className}`}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
                <Box>
                    <Skeleton variant="text" width={150} height={28} />
                    <Skeleton variant="text" width={220} height={20} />
                </Box>
            </Box>
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ mb: 3 }}>
            <Skeleton variant="rounded" width="100%" height={80} sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 3, borderTop: '1px solid #f1f5f9' }}>
            <Skeleton variant="text" width={140} height={30} />
            <Skeleton variant="rounded" width={140} height={40} sx={{ borderRadius: 2 }} />
        </Box>
    </div>
);

export const ListCardSkeleton = ({ rows = 4, className = "" }) => (
    <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col ${className}`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width={180} height={28} />
        </Box>
        <Stack spacing={2}>
            {[...Array(rows)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box>
                            <Skeleton variant="text" width={120} height={24} />
                            <Skeleton variant="text" width={80} height={18} />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="text" width={90} height={20} />
                        <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: 4 }} />
                    </Box>
                </Box>
            ))}
        </Stack>
    </div>
);

export const StatCardSkeleton = () => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 3 }} />
        <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={16} />
            <Skeleton variant="text" width="40%" height={32} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="50%" height={14} sx={{ mt: 1 }} />
        </Box>
    </div>
);

export const CalendarSkeleton = () => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-full">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Skeleton variant="text" width={150} height={32} />
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            {[...Array(7)].map((_, i) => (
                <Skeleton key={i} variant="text" width={30} height={20} />
            ))}
        </Box>
        <Stack spacing={2}>
            {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {[...Array(7)].map((_, j) => (
                        <Skeleton key={j} variant="text" width={30} height={30} />
                    ))}
                </Box>
            ))}
        </Stack>
    </div>
);

export const ProfileSkeleton = () => {
    return (
        <div className="flex flex-col lg:flex-row w-full lg:h-[calc(100vh-70px)] px-4 lg:px-6 pb-4 lg:pb-6 pt-3 gap-3 bg-[#f8fafc]">
            {/* LEFT SIDEBAR SKELETON */}
            <div className="w-full lg:w-[300px] flex-shrink-0 flex flex-col gap-3 lg:max-h-full">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col relative h-full">
                    <div className="p-6 flex flex-col items-center shrink-0">
                        <Skeleton variant="circular" width={112} height={112} sx={{ mb: 2 }} />
                        <Skeleton variant="text" width={150} height={28} />
                        <Skeleton variant="text" width={100} height={20} sx={{ mt: 1 }} />
                    </div>
                    <div className="p-6 pt-0 space-y-5 shrink-0">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <Skeleton variant="circular" width={18} height={18} sx={{ mt: 0.5 }} />
                                <div className="flex-1">
                                    <Skeleton variant="text" width={80} height={16} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* RIGHT FORM CONTENT SKELETON */}
            <div className="flex-1 flex flex-col gap-3 lg:overflow-y-auto lg:pr-2 custom-scrollbar">
                {/* Personal Information */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="text" width={150} height={24} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-full">
                                <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                                <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 2 }} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Contact & Address Information */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex-1">
                        <div className="flex items-center gap-2 mb-4">
                            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 2 }} />
                            <Skeleton variant="text" width={150} height={24} />
                        </div>
                        <div className="space-y-4">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="w-full">
                                    <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                                    <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 2 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex-1">
                        <div className="flex items-center gap-2 mb-4">
                            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 2 }} />
                            <Skeleton variant="text" width={150} height={24} />
                        </div>
                        <div className="space-y-4">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="w-full">
                                    <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                                    <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 2 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Employment Information */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="text" width={150} height={24} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="w-full">
                                <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                                <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 2 }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AccountSettingsSkeleton = () => (
    <div className="flex flex-col w-full px-4 lg:px-6 pb-6 gap-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1 w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="mb-2">
                        <Skeleton variant="text" width={180} height={28} />
                        <Skeleton variant="text" width={260} height={20} />
                    </div>

                    {/* 2FA Section Skeleton */}
                    <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden bg-white p-4 sm:p-5">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Skeleton variant="text" width={160} height={22} />
                                        <Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: 1 }} />
                                    </div>
                                    <Skeleton variant="text" width={280} height={18} sx={{ mt: 0.5 }} />
                                </div>
                            </div>
                            <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: 2 }} />
                        </div>
                    </div>

                    {/* Password Section Skeleton */}
                    <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden bg-white p-4 sm:p-5">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} />
                                <div>
                                    <Skeleton variant="text" width={100} height={22} />
                                    <Skeleton variant="text" width={160} height={18} sx={{ mt: 0.5 }} />
                                </div>
                            </div>
                            <Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 2 }} />
                        </div>
                    </div>

                    {/* Security Tips Skeleton */}
                    <div className="mt-2 bg-blue-50/30 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                        <Skeleton variant="circular" width={20} height={20} sx={{ mt: 0.5 }} />
                        <div className="flex-1">
                            <Skeleton variant="text" width={120} height={22} sx={{ mb: 1.5 }} />
                            <Stack spacing={1.5}>
                                <Skeleton variant="text" width="90%" height={18} />
                                <Skeleton variant="text" width="80%" height={18} />
                                <Skeleton variant="text" width="75%" height={18} />
                            </Stack>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);
